import '@mantine/charts/styles.css'
import { BarChart, type BarChartSeries } from '@mantine/charts'
import { Box } from '@mantine/core'
import { useMemo } from 'react'
import type { BarShapeProps, XAxisTickContentProps } from 'recharts'
import { Rectangle } from 'recharts'
import type { BoggleChallengeVowelRatioBand } from '~/boggle/config/challenges/modTypes'
import {
  BOGGLE_MAX_VOWEL_RATIO_EXCLUSIVE,
  BOGGLE_MIN_VOWEL_RATIO,
} from '~/boggle/config/defaults'
import {
  BOGGLE_LETTER_WEIGHTS,
  flattenBoggleLetterWeights,
} from '~/boggle/config/boardDistributions/letterWeights'
import type { LetterWeight } from '~/boggle/engine/core'
import { partitionLetterWeights } from '~/boggle/engine/standard/refill'

export type LetterDistributionChartRow = {
  letter: string
  Standard: number
  Current: number
}

function chartVowelPartitionRho(vowelRatioBand: BoggleChallengeVowelRatioBand | null | undefined) {
  const min = vowelRatioBand?.minVowelRatio ?? BOGGLE_MIN_VOWEL_RATIO
  const max = vowelRatioBand?.maxVowelRatioExclusive ?? BOGGLE_MAX_VOWEL_RATIO_EXCLUSIVE
  return (min + max) / 2
}

/**
 * Display scores: consonants use **within-consonant** % (100·w/Σw_consonants) so vowel tuning does not
 * renormalize every consonant. Vowels use **ρ·(within-vowel %)** with ρ from the vowel-ratio band so
 * aggregate vowel policy still shows on the chart.
 */
function chartLetterDistributionScores(
  weights: ReadonlyArray<LetterWeight>,
  vowelPartitionRho: number,
): Map<string, number> {
  const rho = vowelPartitionRho
  const { vowels, consonants } = partitionLetterWeights(weights)
  const vowSum = vowels.reduce((sum, [, w]) => sum + w, 0)
  const consSum = consonants.reduce((sum, [, w]) => sum + w, 0)
  const raw = new Map<string, number>()

  if (consSum > 1e-12) {
    for (const [letter, w] of consonants) {
      raw.set(letter, (100 * w) / consSum)
    }
  }

  if (vowSum > 1e-12) {
    for (const [letter, w] of vowels) {
      raw.set(letter, rho * ((100 * w) / vowSum))
    }
  }

  return raw
}

export function buildLetterDistributionChartData(
  currentWeights: ReadonlyArray<LetterWeight>,
  vowelRatioBand?: BoggleChallengeVowelRatioBand | null,
): LetterDistributionChartRow[] {
  const standardMarginals = chartLetterDistributionScores(
    flattenBoggleLetterWeights(BOGGLE_LETTER_WEIGHTS),
    chartVowelPartitionRho(null),
  )
  const currentMarginals = chartLetterDistributionScores(
    currentWeights,
    chartVowelPartitionRho(vowelRatioBand),
  )

  return flattenBoggleLetterWeights(BOGGLE_LETTER_WEIGHTS).map(([letter]) => ({
    letter,
    Standard: standardMarginals.get(letter) ?? 0,
    Current: currentMarginals.get(letter) ?? 0,
  })).sort((a, b) => a.letter.localeCompare(b.letter, 'en'))
}

/** Single series: shape draws Current + Standard overlaid (payload still has both values). */
const SERIES: BarChartSeries[] = [{ name: 'Current', color: 'dark.6' }]

/** Baseline / unchanged bar fill — fixed hex so blends interpolate visibly (Mantine `getThemeColor` returns CSS vars). */
const BAR_BASELINE_GRAY = '#525252'

/** Current/Standard ratio at or above this multiple → full green (`t = 1`) when Current > Standard. */
const LETTER_DIST_RATIO_FULL_GREEN = 1.5
/** Current/Standard ratio at or below this multiple → full yellow (`t = 1`) when Current < Standard. */
const LETTER_DIST_RATIO_FULL_YELLOW = 0.5

/**
 * Standard bar fill only: wider ratio span than {@link letterDistributionChange}’s `t` so the gray → Standard-accent
 * blend is visible; tick/semantics still ramp to full at 1.5× / 0.5×.
 */
const LETTER_DIST_STANDARD_BAR_FULL_RATIO_ABOVE = 1.5
const LETTER_DIST_STANDARD_BAR_FULL_RATIO_BELOW = 0.5

/** Fixed accents: interpolate from grays so gradation is obvious (not Mantine palette luminance-collision). */
const LETTER_DIST_GREEN = '#6dc568'
const LETTER_DIST_GREEN_STANDARD = '#415f42'
const LETTER_DIST_YELLOW = '#fbbf24'
const LETTER_DIST_YELLOW_STANDARD = '#463922'
/** Current = 0 while Standard > 0 (“missing” letter vs baseline). */
const LETTER_DIST_RED = '#8a1c11'
const LETTER_DIST_RED_STANDARD = '#321919'
/** Chart score at which {@link LetterDistributionChange.t} hits 1 for zero-current (consonants: within-pool %; vowels: ρ·within‑vowel %). */
const LETTER_DIST_ZERO_CURRENT_FULL_STANDARD = 4

/** Standard overlay graded from neutral → Standard-accent; Current uses flat accent fills. */
const BAR_STANDARD_GRAY = BAR_BASELINE_GRAY

/** Unchanged marginal letters (∼non-zero probability); ramps up with {@link LetterDistributionChange.t} when changed. */
const LETTER_TICK_MARGINAL_FILL_OPACITY = 0.6
const LETTER_TICK_CHANGED_FILL_OPACITY_CEIL = 1

/** Tick blend start → accent with `change.t` (green / yellow / {@link LETTER_DIST_RED}). */
const TICK_GRAY_UNCHANGED = '#868e96'
/** Mix toward neutral after hue blend so accents read slightly more grayscale. */
const LETTER_TICK_DESAT_GRAY = '#8f9196'
const LETTER_TICK_DESAT_AMOUNT = 0.3

/** Thin by default; bold only when intensity is near full ({@link LetterDistributionChange.t}). */
const LETTER_TICK_WEIGHT_THIN = 400
const LETTER_TICK_WEIGHT_STRONG = 700
const LETTER_TICK_WEIGHT_STRONG_MIN_T = 0.85

function clamp01(value: number) {
  return Math.min(1, Math.max(0, value))
}

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.trim().replace('#', '')
  if (h.length === 8 || h.length === 4) {
    h = h.slice(0, h.length === 8 ? 6 : 3)
  }
  if (h.length === 3) {
    h = h
      .split('')
      .map(ch => ch + ch)
      .join('')
  }
  const n = parseInt(h, 16)
  if (!Number.isFinite(n) || h.length !== 6) {
    return [52, 58, 64]
  }
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${[r, g, b]
    .map(x => {
      const v = Math.min(255, Math.max(0, Math.round(x)))
      return v.toString(16).padStart(2, '0')
    })
    .join('')}`
}

function mixHex(baseHex: string, accentHex: string, t: number) {
  const tt = clamp01(t)
  const [ar, ag, ab] = hexToRgb(baseHex)
  const [br, bg, bb] = hexToRgb(accentHex)
  return rgbToHex(ar + (br - ar) * tt, ag + (bg - ag) * tt, ab + (bb - ab) * tt)
}

function weightsEqual(a: number, b: number) {
  return Math.abs(a - b) < 1e-9
}

export type LetterDistributionChange = {
  kind: 'decrease' | 'equal' | 'increase' | 'zero-current'
  /** 0 = baseline gray only, 1 = full semantic color (ratio-based or zero-current ramp). */
  t: number
}

/**
 * Classify Current vs Standard: ratio-based green/yellow, or **zero-current** red when the letter is absent
 * in the current distribution but the baseline expects a marginal ({@link LETTER_DIST_ZERO_CURRENT_FULL_STANDARD}
 * scales `t` when kind is `zero-current`).
 */
export function letterDistributionChange(current: number, standard: number): LetterDistributionChange {
  if (weightsEqual(current, standard)) {
    return { kind: 'equal', t: 0 }
  }

  const eps = 1e-12

  if (current < eps && standard > eps) {
    return {
      kind: 'zero-current',
      t: clamp01(standard / LETTER_DIST_ZERO_CURRENT_FULL_STANDARD),
    }
  }

  if (standard < eps) {
    if (current > eps) {
      return { kind: 'increase', t: 1 }
    }
    return { kind: 'equal', t: 0 }
  }

  const ratio = current / standard

  if (current > standard) {
    const t = clamp01((ratio - 1) / (LETTER_DIST_RATIO_FULL_GREEN - 1))
    return { kind: 'increase', t }
  }

  const t = clamp01((1 - ratio) / (1 - LETTER_DIST_RATIO_FULL_YELLOW))
  return { kind: 'decrease', t }
}

/** Blend 0…1 for Standard bar only (see {@link LETTER_DIST_STANDARD_BAR_FULL_RATIO_ABOVE} / `_BELOW`). */
function letterDistributionStandardBarBlendT(
  current: number,
  standard: number,
  kind: 'decrease' | 'increase',
): number {
  const eps = 1e-12
  if (standard < eps) {
    return current > eps ? 1 : 0
  }

  const ratio = current / standard

  if (kind === 'increase') {
    return clamp01((ratio - 1) / (LETTER_DIST_STANDARD_BAR_FULL_RATIO_ABOVE - 1))
  }

  return clamp01((1 - ratio) / (1 - LETTER_DIST_STANDARD_BAR_FULL_RATIO_BELOW))
}

/** Same font as board tiles; inline style beats Mantine chart text rules. */
const AXIS_LABEL_FAMILY = `'Noto Sans Variable', sans-serif`
/** Nudge letter labels slightly toward the plot from the category tick anchor. */
const X_AXIS_LABEL_NUDGE_UP_PX = 2

/** Recharts drops to width 0 for 0-valued weights; keep a sliver so the bar still renders. */
const MIN_BAR_LENGTH_PX = 2
/** Bar thickness in the category (horizontal) direction. */
const BAR_THICKNESS = 5

/** Total chart height (plot + room for letter ticks). */
const CHART_HEIGHT_PX = 300
/** Seed for Recharts ResponsiveContainer before ResizeObserver runs (avoids width/height -1 warn). */
const CHART_INITIAL_WIDTH_PX = 360
/** Subtracted from estimated plot height so bars + radius/antialias stay inside the chart box. */
const BAR_AXIS_SPAN_SLOP_PX = 4

function barThicknessPx(slotW: number) {
  return Math.min(BAR_THICKNESS, Math.max(1, slotW))
}

/** Vertical span (px) available for bar lengths: total chart height minus axis/margins (approximate). */
function maxBarAxisSpanPx(
  chartHeightPx: number,
  compactAxis: boolean,
  tickFontSize: number,
) {
  if (compactAxis) {
    const xAxisBand = tickFontSize + 6
    const marginBottom = 2
    return Math.max(
      MIN_BAR_LENGTH_PX,
      chartHeightPx - xAxisBand - marginBottom - BAR_AXIS_SPAN_SLOP_PX,
    )
  }
  /* Default BarChart: small margins + bottom tick band (no xAxisLabel in this screen). */
  return Math.max(MIN_BAR_LENGTH_PX, chartHeightPx - 40 - BAR_AXIS_SPAN_SLOP_PX)
}

/**
 * Pixel height for the Standard overlay. When Current > 0, reuse Recharts' scale via Hc/current.
 * When Current === 0, map Standard using the chart domain (max of Standard vs Current across letters).
 */
function standardPixelHeight(
  hcPx: number,
  current: number,
  standard: number,
  domainMaxValue: number,
  maxBarPixels: number,
) {
  if (standard < 1e-9) return 0
  if (current > 1e-9) {
    return Math.max(MIN_BAR_LENGTH_PX, (hcPx * standard) / current)
  }
  const scale = Math.max(domainMaxValue, 1e-12)
  return Math.max(
    MIN_BAR_LENGTH_PX,
    Math.min(maxBarPixels, (standard / scale) * maxBarPixels),
  )
}

export function LetterDistribution({
  letterWeights,
  vowelRatioBand,
  chartHeightPx = CHART_HEIGHT_PX,
}: {
  letterWeights: ReadonlyArray<LetterWeight>
  vowelRatioBand?: BoggleChallengeVowelRatioBand | null
  /** Total BarChart height (plot + axis tick band). Default {@link CHART_HEIGHT_PX}. */
  chartHeightPx?: number
}) {
  const data = buildLetterDistributionChartData(letterWeights, vowelRatioBand)
  const rowByLetter = useMemo(
    () => new Map(data.map(row => [row.letter, row] as const)),
    [data],
  )
  const domainMaxValue = useMemo(() => {
    let m = 0
    for (const r of data) {
      m = Math.max(m, r.Current, r.Standard)
    }
    return Math.max(m, 1e-12)
  }, [data])
  const compactAxis = chartHeightPx <= 120
  const tickFontSize = compactAxis ? 11 : 13
  const maxBarPixels = maxBarAxisSpanPx(chartHeightPx, compactAxis, tickFontSize)

  return (
    <Box
      h={chartHeightPx}
      mah={chartHeightPx}
      miw={0}
      maw="100%"
      w="100%"
      style={{ lineHeight: 0, overflow: 'hidden' }}
    >
      <BarChart
        attributes={{
          container: {
            initialDimension: {
              width: CHART_INITIAL_WIDTH_PX,
              height: chartHeightPx,
            },
          },
        }}
        barChartProps={{
          barCategoryGap: 0,
          barGap: 0,
          style: { overflow: 'hidden' },
          tabIndex: -1,
          ...(compactAxis
            ? {
                /* Tight layout for inline board strip; axis labels sit in xAxis height, not margin. */
                margin: { top: 0, right: 0, bottom: 2, left: 0 },
              }
            : {}),
        }}
        barProps={{
          shape: (shapeProps: BarShapeProps) => {
            const payload = shapeProps.payload as LetterDistributionChartRow
            const c = payload.Current
            const s = payload.Standard
            const change = letterDistributionChange(c, s)
            const slotW = shapeProps.width
            const Hc = Math.max(shapeProps.height, MIN_BAR_LENGTH_PX)
            const xBase = Number(shapeProps.x)
            const yBase = Number(shapeProps.y)
            const midX = xBase + slotW / 2
            const thickness = barThicknessPx(slotW)
            const xAdj = midX - thickness / 2
            const radius: [number, number, number, number] = [3, 3, 0, 0]

            if (change.kind === 'equal') {
              return (
                <Rectangle
                  fill={BAR_BASELINE_GRAY}
                  fillOpacity={1}
                  height={Hc}
                  radius={radius}
                  width={thickness}
                  x={xAdj}
                  y={yBase}
                />
              )
            }

            const Hs = standardPixelHeight(Hc, c, s, domainMaxValue, maxBarPixels)
            const standardBlendT =
              change.kind === 'zero-current'
                ? change.t
                : letterDistributionStandardBarBlendT(c, s, change.kind)
            const standardFill =
              change.kind === 'zero-current'
                ? mixHex(BAR_STANDARD_GRAY, LETTER_DIST_RED_STANDARD, standardBlendT)
                : change.kind === 'increase'
                  ? mixHex(BAR_STANDARD_GRAY, LETTER_DIST_GREEN_STANDARD, standardBlendT)
                  : mixHex(BAR_STANDARD_GRAY, LETTER_DIST_YELLOW_STANDARD, standardBlendT)
            const currentFill =
              change.kind === 'zero-current'
                ? LETTER_DIST_RED
                : change.kind === 'increase'
                  ? LETTER_DIST_GREEN
                  : LETTER_DIST_YELLOW

            const currentRect = (
              <Rectangle
                key="current"
                fill={currentFill}
                fillOpacity={1}
                height={Hc}
                radius={radius}
                width={thickness}
                x={xAdj}
                y={yBase}
              />
            )
            const standardRect = (
              <Rectangle
                key="standard"
                fill={standardFill}
                fillOpacity={1}
                height={Hs}
                radius={radius}
                width={thickness}
                x={xAdj}
                y={yBase + Hc - Hs}
              />
            )

            /* Current > Standard: standard shorter, drawn on top (second). */
            if (change.kind === 'increase') {
              return (
                <>
                  {currentRect}
                  {standardRect}
                </>
              )
            }
            /* Current < Standard: standard taller beneath (first). */
            return (
              <>
                {standardRect}
                {currentRect}
              </>
            )
          },
        }}
        data={data}
        minBarSize={MIN_BAR_LENGTH_PX}
        dataKey="letter"
        gridAxis="x"
        gridColor="transparent"
        textColor="white"
        h={chartHeightPx}
        miw={0}
        w="100%"
        orientation="horizontal"
        series={SERIES}
        withLegend={false}
        withTooltip={false}
        tickLine="none"
        withXAxis
        withYAxis
        xAxisProps={{
          interval: 0,
          minTickGap: 0,
          ...(compactAxis
            ? {
                height: tickFontSize + 6,
                orientation: 'bottom',
                tickMargin: 0,
                padding: { left: 0, right: 0 },
              }
            : {}),
          tick: (props: XAxisTickContentProps) => {
            const letter = String(props.payload.value)
            const row = rowByLetter.get(letter)
            const current = row?.Current ?? 0
            const standard = row?.Standard ?? 0
            const zeroProb = Math.abs(current) < 1e-9
            const change = letterDistributionChange(current, standard)
            const x = Number(props.x)
            const y = Number(props.y) - (compactAxis ? 0 : X_AXIS_LABEL_NUDGE_UP_PX)
            const fillOpacity =
              change.kind === 'equal'
                ? zeroProb
                  ? 0.25
                  : LETTER_TICK_MARGINAL_FILL_OPACITY
                : LETTER_TICK_MARGINAL_FILL_OPACITY +
                  (LETTER_TICK_CHANGED_FILL_OPACITY_CEIL - LETTER_TICK_MARGINAL_FILL_OPACITY) *
                    change.t
            const tickAccent =
              change.kind === 'increase'
                ? LETTER_DIST_GREEN
                : change.kind === 'zero-current'
                  ? LETTER_DIST_RED
                  : LETTER_DIST_YELLOW
            const tickFill =
              change.kind === 'equal'
                ? TICK_GRAY_UNCHANGED
                : mixHex(
                    mixHex(TICK_GRAY_UNCHANGED, tickAccent, change.t),
                    LETTER_TICK_DESAT_GRAY,
                    LETTER_TICK_DESAT_AMOUNT,
                  )
            return (
              <text
                dominantBaseline="hanging"
                fill={tickFill}
                fillOpacity={fillOpacity}
                fontSize={tickFontSize}
                fontWeight={
                  change.kind !== 'equal' && change.t >= LETTER_TICK_WEIGHT_STRONG_MIN_T
                    ? LETTER_TICK_WEIGHT_STRONG
                    : LETTER_TICK_WEIGHT_THIN
                }
                style={{ fontFamily: AXIS_LABEL_FAMILY }}
                textAnchor="middle"
                x={x}
                y={y}
              >
                {letter}
              </text>
            )
          },
        }}
        yAxisProps={{
          axisLine: false,
          domain: [0, domainMaxValue],
          tick: false,
          width: 0,
        }}
      />
    </Box>
  )
}
